import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import {
  type CustomerRow,
  type GuestTag,
  resolveGuestTag,
  useCustomersQuery,
} from '../../hooks/useCustomersQuery';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { useSlashSearchShortcut } from '../../hooks/useSlashSearchShortcut';
import { formatPhone } from '../../shared/phone';
import { formatShortDate } from '../../shared/time';
import {
  Avatar,
  Callout,
  EmptyState,
  HUE_TINT,
  type Hue,
  IconCalendar,
  IconReceipt,
  IconSearch,
  IconUsers,
  Input,
  Kbd,
  ListPage,
  PageHeaderBand,
  Pagination,
  Skeleton,
  Spinner,
  SummaryFact,
  SummaryFacts,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import { phoneGlyph } from '../SmsInbox/smsModel';
import { formatDollars } from './orderModel';

interface CustomersCountResult {
  count: number;
  /** Distinct North American area codes across the store's guests; null when the API doesn't report it. */
  areaCodes: number | null;
}

async function fetchCustomerCount(): Promise<CustomersCountResult> {
  const response = await api.get(apiEndpoints.getCustomersCount());
  if (!response?.ok) {
    return { count: 0, areaCodes: null };
  }

  const data = await response.json();
  return {
    count: typeof data.count === 'number' ? data.count : 0,
    areaCodes: typeof data.area_codes === 'number' ? data.area_codes : null,
  };
}

const PAGE_SIZE = 25;

const DEFAULT_CUSTOMERS_ORDERING = '-created_at';

/** UI column key -> API `ordering` field name. */
const CUSTOMER_SORT_UI_TO_FIELD: Record<string, string> = {
  customer: 'name',
  total_spent: 'total_spent',
  total_orders: 'total_orders',
  opentable_reservations: 'opentable_reservations',
  customer_since: 'created_at',
};

const CUSTOMER_SORT_FIELD_TO_UI: Record<string, string> = Object.fromEntries(
  Object.entries(CUSTOMER_SORT_UI_TO_FIELD).map(([k, v]) => [v, k]),
);

/**
 * Reading order, as on Reservations: who the guest is, how to reach them,
 * what they've done with us, how long they've been coming. The customer ID
 * comes last in quieter type.
 */
const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  align?: 'left' | 'right';
  sortable?: boolean;
}[] = [
  { key: 'customer', label: 'Customer', sortable: true },
  { key: 'status', label: 'Status', width: 148 },
  { key: 'phone', label: 'Phone', width: 140 },
  {
    key: 'total_spent',
    label: 'Total spent',
    width: 112,
    align: 'right',
    sortable: true,
  },
  {
    key: 'total_orders',
    label: 'Total orders',
    width: 112,
    align: 'right',
    sortable: true,
  },
  {
    key: 'opentable_reservations',
    label: 'Reservations',
    width: 120,
    align: 'right',
    sortable: true,
  },
  {
    key: 'customer_since',
    label: 'Customer since',
    width: 152,
    sortable: true,
  },
  { key: 'customer_id', label: 'Customer ID', width: 112 },
];

const isUnknownCustomer = (name: string | null) =>
  !name?.trim() || name.trim().toLowerCase() === 'unknown';

/** Same capsule as order type and status. VIP is amber, repeat is accent, new is neutral. */
const GUEST_TAG: Record<
  GuestTag,
  { label: string; tone: 'warning' | 'accent' | 'neutral' }
> = {
  vip: { label: 'VIP', tone: 'warning' },
  repeat: { label: 'Repeat guest', tone: 'accent' },
  new: { label: 'New guest', tone: 'neutral' },
};

const MissingValue: React.FC = () => <span className="text-v2-fg-4">—</span>;

/** A tally that reads as "none yet" at zero rather than shouting a 0. */
const Tally: React.FC<{ value: React.ReactNode; zero: boolean }> = ({
  value,
  zero,
}) => <span className={clsx(zero && 'text-v2-fg-4')}>{value}</span>;

/**
 * A non-zero order or reservation count in its dashboard hue, so the guests
 * who actually order and book stand out from the callers who never have.
 */
const CountChip: React.FC<{
  value: number;
  hue: Hue;
  icon: React.ReactNode;
  label: string;
}> = ({ value, hue, icon, label }) =>
  value === 0 ? (
    <Tally value={0} zero />
  ) : (
    <span
      aria-label={label}
      className={clsx(
        'v2-num inline-flex h-6 items-center gap-1 rounded-v2 px-1.5 text-v2-12 font-semibold',
        HUE_TINT[hue],
      )}
    >
      {icon}
      {value}
    </span>
  );

export default function CustomerTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderingParam =
    searchParams.get('ordering') ?? DEFAULT_CUSTOMERS_ORDERING;
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashSearchShortcut(searchRef);

  const { sortKey, sortOrder } = useMemo(() => {
    const desc = orderingParam.startsWith('-');
    const field = desc ? orderingParam.slice(1) : orderingParam;
    return {
      sortKey: CUSTOMER_SORT_FIELD_TO_UI[field] ?? 'customer',
      sortOrder: (desc ? 'desc' : 'asc') as 'asc' | 'desc',
    };
  }, [orderingParam]);

  const { data: stats, isPending: statsLoading } = useQuery({
    queryKey: useScopedQueryKey('customer-stats'),
    queryFn: fetchCustomerCount,
  });
  const customerCount = stats?.count ?? 0;
  const areaCodeCount = stats?.areaCodes ?? 0;
  const showCustomerCount = statsLoading || customerCount > 0;
  // Stay hidden until the count endpoint reports a positive number. A loading
  // skeleton would flash, then vanish, on the production response that omits
  // area_codes (or returns 0) until the backend ships the field.
  const showAreaCodes = areaCodeCount > 0;

  const { data, isPending, isError, error, isFetching } = useCustomersQuery(
    page,
    PAGE_SIZE,
    true,
    orderingParam,
    debouncedSearch,
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const toggleSort = useCallback(
    (key: string) => {
      const targetField = CUSTOMER_SORT_UI_TO_FIELD[key];
      if (!targetField) return;
      setPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const prevRaw = prev.get('ordering') ?? DEFAULT_CUSTOMERS_ORDERING;
        const prevDesc = prevRaw.startsWith('-');
        const prevField = prevDesc ? prevRaw.slice(1) : prevRaw;
        const newDesc = prevField !== targetField ? false : !prevDesc;
        next.set('ordering', newDesc ? `-${targetField}` : targetField);
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    setPage(1);
  };

  const customers: CustomerRow[] = data?.customers ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showInitialLoading = isPending && customers.length === 0;
  const fetchError =
    isError && error instanceof Error
      ? error.message
      : isError
        ? 'An unexpected error occurred. Please try again.'
        : null;
  const showPagination =
    !showInitialLoading && !fetchError && customers.length > 0;
  const isRefreshing =
    isFetching && !showInitialLoading && !fetchError && customers.length > 0;
  const emptyNoun = debouncedSearch ? 'matching customers' : 'customers';

  return (
    <>
      <PageMeta
        title="AskArthur | Customers"
        description="AskArthur Customer Page"
      />

      <ListPage
        title="Customers"
        actions={
          isRefreshing ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
            >
              <Spinner />
              Updating customers...
            </span>
          ) : null
        }
        summary={
          (showCustomerCount || showAreaCodes) && (
            <SummaryFacts>
              {showCustomerCount && (
                <SummaryFact
                  label={customerCount === 1 ? 'Customer' : 'Customers'}
                  value={
                    statsLoading ? (
                      <Skeleton width={48} className="h-7" />
                    ) : (
                      customerCount.toLocaleString()
                    )
                  }
                />
              )}
              {showAreaCodes && (
                <SummaryFact
                  label={areaCodeCount === 1 ? 'Area code' : 'Area codes'}
                  value={areaCodeCount.toLocaleString()}
                />
              )}
            </SummaryFacts>
          )
        }
        toolbar={
          <PageHeaderBand>
            <div className="w-full min-w-0 sm:w-[320px] sm:shrink">
              <Input
                ref={searchRef}
                id="customers-search"
                type="search"
                aria-label="Search by name or phone"
                placeholder="Find a guest by name or phone…"
                value={searchInput}
                onChange={handleSearchChange}
                leading={<IconSearch size={14} />}
                trailing={
                  <span className="hidden sm:inline-flex">
                    <Kbd>/</Kbd>
                  </span>
                }
              />
            </div>
          </PageHeaderBand>
        }
      >
        {fetchError ? (
          <div className="p-4 lg:p-6">
            <Callout tone="danger" title="Couldn't load customers">
              {fetchError}
            </Callout>
          </div>
        ) : !showInitialLoading && customers.length === 0 ? (
          <EmptyState
            icon={<IconUsers />}
            title={`You don't have any ${emptyNoun} at the moment`}
            description={`When ${emptyNoun} appear, they'll show up here.`}
          />
        ) : (
          <>
            {showInitialLoading && (
              <p role="status" className="sr-only">
                Loading customers…
              </p>
            )}
            <div
              className={clsx(
                'v2-scroll overflow-x-auto transition-opacity',
                isRefreshing && 'opacity-70',
              )}
            >
              <Table fixed className="min-w-[1100px]">
                <THead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <TH
                        key={column.key}
                        width={column.width}
                        align={column.align}
                        sortable={column.sortable && !showInitialLoading}
                        active={column.sortable && sortKey === column.key}
                        dir={sortOrder}
                        onSort={() => toggleSort(column.key)}
                      >
                        {column.label}
                      </TH>
                    ))}
                  </tr>
                </THead>
                <TBody>
                  {showInitialLoading ? (
                    <TableSkeletonRows cols={COLUMNS.length} size="lg" />
                  ) : (
                    customers.map((customer) => {
                      const spent = customer.total_spent || 0;
                      const orders = customer.total_orders || 0;
                      const reservations = customer.opentable_reservations || 0;
                      const guestTagKey = resolveGuestTag(customer);
                      const guestTag = GUEST_TAG[guestTagKey];
                      return (
                        <TR size="lg" key={customer.customer_id}>
                          <TD size="lg" className="overflow-hidden">
                            <div className="flex min-w-0 items-center gap-2">
                              <Avatar
                                name={
                                  isUnknownCustomer(customer.name)
                                    ? null
                                    : customer.name
                                }
                                identity={customer.phone}
                                fallback={phoneGlyph(customer.phone)}
                                hue="auto"
                                className="mr-0.5"
                              />
                              {isUnknownCustomer(customer.name) ? (
                                <span className="truncate text-v2-fg-3">
                                  Unknown guest
                                </span>
                              ) : (
                                <span className="truncate font-semibold text-v2-fg">
                                  {customer.name?.trim()}
                                </span>
                              )}
                            </div>
                          </TD>
                          <TD size="lg">
                            <span
                              title={
                                guestTagKey === 'vip'
                                  ? customer.vip_name
                                    ? `VIP: ${customer.vip_name}`
                                    : 'VIP guest'
                                  : undefined
                              }
                            >
                              <Tag tone={guestTag.tone}>{guestTag.label}</Tag>
                            </span>
                          </TD>
                          <TD
                            size="lg"
                            num
                            className="whitespace-nowrap text-v2-fg-2"
                          >
                            {formatPhone(customer.phone) || <MissingValue />}
                          </TD>
                          <TD
                            size="lg"
                            num
                            align="right"
                            className="font-medium"
                          >
                            <Tally
                              value={formatDollars(spent)}
                              zero={spent === 0}
                            />
                          </TD>
                          <TD size="lg" num align="right">
                            <CountChip
                              value={orders}
                              hue="teal"
                              icon={<IconReceipt size={12} />}
                              label={`${orders} ${orders === 1 ? 'order' : 'orders'}`}
                            />
                          </TD>
                          <TD size="lg" num align="right">
                            <CountChip
                              value={reservations}
                              hue="amber"
                              icon={<IconCalendar size={12} />}
                              label={`${reservations} ${reservations === 1 ? 'reservation' : 'reservations'}`}
                            />
                          </TD>
                          <TD
                            size="lg"
                            num
                            className="whitespace-nowrap text-v2-fg-2"
                          >
                            {formatShortDate(customer.customer_since) || (
                              <MissingValue />
                            )}
                          </TD>
                          <TD size="lg" num className="text-v2-12 text-v2-fg-3">
                            {customer.customer_id}
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </TBody>
              </Table>
            </div>
            {showPagination && (
              <Pagination
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPage={setPage}
                noun="customers"
              />
            )}
          </>
        )}
      </ListPage>
    </>
  );
}
